import { ALERT_ACTORS, ALERT_SEVERITIES, CARGO_STATES, SHIPMENT_STATUSES } from '@plancia/contracts';
import { z } from 'zod';
import { api } from './client';

// The shipments area of the internal API (07 S01–S09), mirrored from
// apps/api/src/modules/shipments/console.dto.ts, actions.dto.ts,
// reads/reads-console.dto.ts and outbound (PayloadView/NoteView).
// Unknown keys pass through; enums the console only displays stay strings.

const place = z.looseObject({
  raw: z.string().nullable(),
  location_id: z.string().nullable(),
  name: z.string().nullable(),
  unlocode: z.string().nullable(),
});

export const alertChipSchema = z.looseObject({
  id: z.string(),
  code: z.string(),
  severity: z.enum(ALERT_SEVERITIES),
  actor: z.enum(ALERT_ACTORS),
  params: z.record(z.string(), z.string()),
  opened_at: z.string(),
  acknowledged_at: z.string().nullable(),
  snoozed_until: z.string().nullable(),
});
export type AlertChipView = z.infer<typeof alertChipSchema>;

export const shipmentListItemSchema = z.looseObject({
  id: z.string(),
  dokless_id: z.string(),
  reference: z.string().nullable(),
  reference_type: z.string().nullable(),
  containers: z.array(z.string()),
  bill_of_lading: z.string().nullable(),
  booking: z.string().nullable(),
  carrier: z.looseObject({ id: z.string(), code: z.string(), name: z.string() }).nullable(),
  carrier_code: z.string().nullable(),
  carrier_code_raw: z.string().nullable(),
  transport_mode: z.string(),
  read_mode: z.string().nullable(),
  origin: place,
  destination: place,
  status: z.enum(SHIPMENT_STATUSES),
  cargo_state: z.enum(CARGO_STATES),
  attention: z.boolean(),
  attention_since: z.string().nullable(),
  closed_reason: z.string().nullable(),
  closed_at: z.string().nullable(),
  last_fact_code: z.string().nullable(),
  last_fact_at: z.string().nullable(),
  etd: z.string().nullable(),
  eta_dokless: z.string().nullable(),
  carrier_eta: z.string().nullable(),
  plancia_eta: z.string().nullable(),
  plancia_eta_reliable: z.boolean().nullable(),
  conveyance: z
    .looseObject({ id: z.string(), name: z.string(), last_position_at: z.string().nullable() })
    .nullable(),
  alerts: z.array(alertChipSchema),
  open_alerts: z.number(),
  next_check_at: z.string().nullable(),
  next_check_reason: z.string().nullable(),
  /** The cadence's hours between checks (the API keeps them with the next check). */
  cadence_hours: z.number().nullable().optional(),
  overdue: z.boolean(),
  last_read_at: z.string().nullable(),
  last_successful_read_at: z.string().nullable(),
  consecutive_failures: z.number(),
  removed_from_queue: z.boolean(),
  revision: z.looseObject({ current: z.number(), last_delivered: z.number().nullable() }),
  updated_at: z.string(),
});
export type ShipmentListItem = z.infer<typeof shipmentListItemSchema>;

export const shipmentListSchema = z.looseObject({
  items: z.array(shipmentListItemSchema),
  next_cursor: z.string().nullable(),
});
export type ShipmentList = z.infer<typeof shipmentListSchema>;

export const countersSchema = z.looseObject({
  open: z.number(),
  by_status: z.record(z.string(), z.number()),
  attention: z.number(),
  never_read: z.number(),
  arriving_48h: z.number(),
  overdue: z.number(),
  closed_last_7_days: z.number(),
  attention_by_severity: z
    .looseObject({ blocking: z.number(), warning: z.number(), info: z.number() })
    .optional(),
});
export type Counters = z.infer<typeof countersSchema>;

export const savedViewSchema = z.looseObject({
  id: z.string(),
  name: z.string(),
  filters: z.record(z.string(), z.string()),
  sort: z.string().nullable(),
  created_at: z.string(),
  updated_at: z.string(),
});
export type SavedView = z.infer<typeof savedViewSchema>;
export const savedViewListSchema = z.looseObject({ items: z.array(savedViewSchema) });

// ── detail ─────────────────────────────────────────────────────────────

const locationPoint = z.looseObject({
  location_id: z.string().nullable(),
  raw: z.string().nullable(),
  name: z.string().nullable(),
  unlocode: z.string().nullable(),
  lat: z.number().nullable(),
  lon: z.number().nullable(),
});
export type LocationPoint = z.infer<typeof locationPoint>;

export const eventViewSchema = z.looseObject({
  id: z.string(),
  code: z.string(),
  status: z.string(),
  occurred_at: z.string().nullable(),
  date_only: z.boolean(),
  location: z.string().nullable(),
  location_id: z.string().nullable(),
  container: z.string().nullable(),
  vessel: z.string().nullable(),
  voyage: z.string().nullable(),
  source: z.string(),
  observed_by: z.string(),
  confirmed_by: z.unknown(),
  read_id: z.string().nullable(),
  original_text: z.string().nullable(),
  withdrawn_at: z.string().nullable(),
  withdrawn_reason: z.string().nullable(),
  created_at: z.string(),
  /** The place resolved to a location; null when unresolved. */
  place: z.looseObject({ name: z.string(), unlocode: z.string().nullable() }).nullable().optional(),
  /** When the read that brought it started. */
  read_at: z.string().nullable().optional(),
  /** The first full payload Dokless accepted after the row was recorded. */
  dokless_revision: z.number().nullable().optional(),
});
export type EventView = z.infer<typeof eventViewSchema>;

export const legViewSchema = z.looseObject({
  id: z.string(),
  seq: z.number(),
  from: locationPoint,
  to: locationPoint,
  vessel: z.string().nullable(),
  conveyance_id: z.string().nullable(),
  voyage: z.string().nullable(),
  departure: z.looseObject({ planned: z.string().nullable(), actual: z.string().nullable() }),
  arrival: z.looseObject({ planned: z.string().nullable(), actual: z.string().nullable() }),
  status: z.string(),
  source: z.string(),
  transshipment: z.boolean(),
  blocked_by: z.unknown(),
  current: z.boolean(),
});
export type LegView = z.infer<typeof legViewSchema>;

export const routeViewSchema = z.looseObject({
  sailed: z.array(z.tuple([z.number(), z.number()])),
  /** The road sailed piece by piece, guessed pieces marked (ADR 0076); null on an older recompute. */
  sailed_segments: z
    .array(z.object({ estimated: z.boolean(), points: z.array(z.tuple([z.number(), z.number()])) }))
    .nullable()
    .optional(),
  via: z.string().nullable().optional(),
  ahead: z.array(z.tuple([z.number(), z.number()])),
  ports: z.array(
    z.looseObject({
      name: z.string().nullable(),
      unlocode: z.string().nullable(),
      lat: z.number().nullable(),
      lon: z.number().nullable(),
      role: z.enum(['origin', 'transshipment', 'destination']),
      done: z.boolean(),
    }),
  ),
  blocked: z.unknown(),
});
export type RouteView = z.infer<typeof routeViewSchema>;

export const positionViewSchema = z.looseObject({
  lat: z.number(),
  lon: z.number(),
  at: z.string(),
  speed_knots: z.number().nullable(),
  course_deg: z.number().nullable(),
  heading_deg: z.number().nullable(),
  nav_status: z.number().nullable(),
  destination_text: z.string().nullable(),
  ais_eta: z.string().nullable(),
  precision: z.enum(['decimals', 'degrees', 'map']),
  /** `precision: map` (ADR 0099): how far the real position may be, in km. */
  radius_km: z.number().nullable().optional(),
  source: z.string(),
  stale: z.boolean(),
});
export type PositionView = z.infer<typeof positionViewSchema>;

export const conveyanceViewSchema = z.looseObject({
  id: z.string(),
  kind: z.string(),
  name: z.string(),
  imo: z.string().nullable(),
  mmsi: z.string().nullable(),
  ais_status: z.unknown(),
  ais_status_at: z.string().nullable(),
  position: positionViewSchema.nullable(),
  no_position_reason: z.string().nullable(),
});
export type ConveyanceDetail = z.infer<typeof conveyanceViewSchema>;

export const alertDetailSchema = alertChipSchema.extend({
  closed_at: z.string().nullable(),
  closed_reason: z.string().nullable(),
});
export type AlertDetail = z.infer<typeof alertDetailSchema>;

export const transitionSchema = z.looseObject({
  from_status: z.string().nullable(),
  to_status: z.string(),
  from_cargo_state: z.string().nullable(),
  to_cargo_state: z.string().nullable(),
  reason: z.string(),
  evidence: z.unknown(),
  triggered_by: z.string(),
  at: z.string(),
});

export const routePassageSchema = z.looseObject({
  code: z.string(),
  name: z.string(),
  state: z.string(),
  since: z.string().nullable(),
  crossed: z.boolean(),
  ahead: z.boolean(),
  lat: z.number(),
  lon: z.number(),
});
export type RoutePassage = z.infer<typeof routePassageSchema>;
export const portCallViewSchema = z.looseObject({
  port: z.string(),
  unlocode: z.string().nullable(),
  country: z.string().nullable(),
  lat: z.number().nullable(),
  lon: z.number().nullable(),
  arrival_at: z.string().nullable(),
  departure_at: z.string().nullable(),
  source: z.string(),
});
export const vesselIdentitySchema = z.looseObject({
  name: z.string().nullable(),
  state: z.enum(['certain', 'ambiguous', 'pinned', 'none']),
  candidates: z.array(
    z.looseObject({
      id: z.string(),
      name: z.string(),
      imo: z.string().nullable(),
      mmsi: z.string().nullable(),
      last_position_at: z.string().nullable(),
    }),
  ),
  pinned: z
    .looseObject({ conveyance_id: z.string(), at: z.string().nullable(), by: z.string().nullable() })
    .nullable(),
});
export type VesselIdentity = z.infer<typeof vesselIdentitySchema>;

export const shipmentDetailSchema = shipmentListItemSchema.extend({
  containers_detail: z.array(
    z.looseObject({
      number: z.string(),
      iso_valid: z.boolean(),
      check_digit_expected: z.number().nullable(),
    }),
  ),
  origin_point: locationPoint,
  destination_point: locationPoint,
  booked_vessel: z.string().nullable(),
  booked_voyage: z.string().nullable(),
  start_url: z.string().nullable(),
  notes: z.string().nullable(),
  failing_since: z.string().nullable(),
  reopened_count: z.number(),
  created_at: z.string(),
  dokless_traceable_status: z.string().nullable(),
  dokless_next_check_at: z.string().nullable(),
  events: z.array(eventViewSchema),
  legs: z.array(legViewSchema),
  route: routeViewSchema.nullable(),
  conveyance_detail: conveyanceViewSchema.nullable(),
  alerts_all: z.array(alertDetailSchema),
  latest_estimate: z.unknown(),
  passages: z.array(routePassageSchema).optional(),
  port_calls: z.array(portCallViewSchema).optional(),
  vessel_identity: vesselIdentitySchema.optional(),
  transitions: z.array(transitionSchema),
});
export type ShipmentDetail = z.infer<typeof shipmentDetailSchema>;

export const eventsSchema = z.looseObject({
  items: z.array(eventViewSchema),
  withdrawn: z.array(eventViewSchema),
  containers: z.array(z.string()),
});
export type EventsAnswer = z.infer<typeof eventsSchema>;

export const etaEstimateSchema = z.looseObject({
  id: z.string(),
  computed_at: z.string(),
  at: z.string(),
  basis: z.string(),
  port: z.string().nullable(),
  distance_km: z.number().nullable(),
  speed_knots: z.number().nullable(),
  position_at: z.string().nullable(),
  carrier_eta: z.string().nullable(),
  diff_hours: z.number().nullable(),
  tolerance_hours: z.number(),
  reliable: z.boolean(),
  unreliable_reasons: z.unknown(),
  via_passages: z.unknown(),
});
export type EtaEstimate = z.infer<typeof etaEstimateSchema>;
export const carrierForecastSchema = z.looseObject({
  /** A carrier time as written (ADR 0055). */
  forecast_at: z.string(),
  place: z.string().nullable().optional(),
  first_seen_at: z.string(),
  last_seen_at: z.string().nullable().optional(),
  seen_count: z.number().optional(),
});
export const etaEstimatesSchema = z.looseObject({
  items: z.array(etaEstimateSchema),
  /** The carrier's ETA history (forecasts_seen), oldest first. */
  carrier_history: z.array(carrierForecastSchema).optional(),
});

export const HISTORY_KINDS = ['transition', 'alert', 'audit', 'notification', 'activity'] as const;
export const historyItemSchema = z.looseObject({
  at: z.string(),
  kind: z.enum(HISTORY_KINDS),
  summary: z.string(),
  who: z.string().nullable(),
  details: z.unknown(),
});
export type HistoryItem = z.infer<typeof historyItemSchema>;
export const historySchema = z.looseObject({
  closed_because: z.string().nullable(),
  /** The parts of «why closed», written in the console's language. */
  closed: z
    .looseObject({
      reason: z.string().nullable(),
      at: z.string().nullable(),
      by: z.string().nullable(),
      rule: z.string().nullable(),
      evidence: z.unknown(),
    })
    .nullable()
    .optional(),
  items: z.array(historyItemSchema),
});
export type HistoryAnswer = z.infer<typeof historySchema>;

/** `GET /internal/shipments/:id/positions`: the followed conveyance's fixes, oldest first. */
export const positionsSchema = z.looseObject({
  conveyance_id: z.string().nullable(),
  from: z.string().nullable().optional(),
  /** The trail ends here (the cargo left the vessel); null while it is on board. */
  until: z.string().nullable().optional(),
  items: z.array(positionViewSchema),
});

// ── reads (S05) ────────────────────────────────────────────────────────

const attempt = z.looseObject({
  at: z.string(),
  state: z.string(),
  status: z.number().nullable(),
  error: z.string().nullable(),
});
export type DeliveryAttempt = z.infer<typeof attempt>;

const blob = z.looseObject({ url: z.string().nullable(), pruned: z.boolean(), bytes: z.number().nullable() });

export const readSummarySchema = z.looseObject({
  id: z.string(),
  shipment_id: z.string(),
  carrier: z.looseObject({ id: z.string(), code: z.string(), name: z.string() }),
  mode: z.string().nullable(),
  recipe: z.looseObject({ id: z.string(), version: z.number(), status: z.string() }).nullable(),
  searched_with: z.looseObject({ key: z.string().nullable(), value: z.string().nullable() }),
  started_at: z.string(),
  finished_at: z.string().nullable(),
  duration_ms: z.number().nullable(),
  outcome: z.string(),
  failure: z.string().nullable(),
  failed_step: z.number().nullable(),
  events_read: z.number().nullable(),
  events_new: z.number().nullable(),
  cost_usd: z.number().nullable(),
  extension_client: z.string().nullable(),
  retry_of_read_id: z.string().nullable(),
  dry_run: z.boolean(),
  failure_detail: z.string().nullable().optional(),
  reported_wrong: z
    .looseObject({ at: z.string(), by: z.string().nullable(), reason: z.string().nullable() })
    .nullable()
    .optional(),
});
export type ReadSummary = z.infer<typeof readSummarySchema>;
export const readQueueSchema = z.looseObject({
  position: z.number(),
  of: z.number(),
  carrier_position: z.number().nullable(),
  carrier_of: z.number().nullable(),
  next_check_at: z.string().nullable(),
});
export type ReadQueue = z.infer<typeof readQueueSchema>;
export const readListSchema = z.looseObject({
  items: z.array(readSummarySchema),
  next_cursor: z.string().nullable(),
  /** Where the next read stands in the queue; null when the shipment is not followed. */
  queue: readQueueSchema.nullable().optional(),
});
export const readDetailSchema = readSummarySchema.extend({
  url: z.string().nullable(),
  failure_detail: z.string().nullable(),
  extracted: z.unknown(),
  extraction_model: z.string().nullable(),
  verification: z.unknown(),
  model_steps: z.unknown(),
  reuse_verdict: z.string().nullable(),
  blobs: z.looseObject({ screenshot: blob, html: blob, text: blob, tables: blob, network: blob }),
  pruned: z.boolean(),
});
export type ReadDetail = z.infer<typeof readDetailSchema>;

// ── deliveries (S06) ───────────────────────────────────────────────────

export const payloadViewSchema = z.looseObject({
  id: z.string(),
  shipment_id: z.string(),
  revision: z.number(),
  kind: z.string(),
  cause: z.string(),
  state: z.string(),
  body_hash: z.string(),
  attempts: z.number(),
  next_attempt_at: z.string().nullable(),
  sent_at: z.string().nullable(),
  response_status: z.number().nullable(),
  response_body: z.unknown().optional(),
  error: z.string().nullable(),
  superseded_by_id: z.string().nullable(),
  created_at: z.string(),
  /** The body's size in bytes. */
  size_bytes: z.number().optional(),
  /** Every attempt, oldest first. */
  attempt_log: z.array(attempt).optional(),
  body: z.unknown().optional(),
});
export type PayloadView = z.infer<typeof payloadViewSchema>;
export const noteViewSchema = z.looseObject({
  id: z.string(),
  shipment_id: z.string(),
  level: z.string(),
  kind: z.string(),
  message: z.string(),
  handover: z.boolean(),
  resume: z.boolean(),
  state: z.string(),
  attempts: z.number(),
  sent_at: z.string().nullable(),
  response_status: z.number().nullable(),
  error: z.string().nullable(),
  created_at: z.string(),
  /** The shipment's revision when the note was written. */
  revision: z.number().nullable().optional(),
  attempt_log: z.array(attempt).optional(),
});
export type NoteView = z.infer<typeof noteViewSchema>;
export const shipmentDeliveriesSchema = z.looseObject({
  payloads: z.array(payloadViewSchema),
  notes: z.array(noteViewSchema),
});

// ── actions (S08, S01 bulk) ────────────────────────────────────────────

export type ShipmentActionBody =
  | { action: 'track_now' }
  | { action: 'acknowledge'; alert_ids?: string[] }
  | { action: 'snooze'; until: string; alert_ids?: string[] }
  | { action: 'note'; text: string | null }
  | { action: 'resend' }
  | { action: 'set_read_mode'; read_mode: 'auto' | 'recipe' | 'model'; start_url?: string | null }
  | { action: 'release_to_manual'; reason: string }
  | { action: 'remove_from_queue'; reason: string }
  | { action: 'resume' }
  | { action: 'close'; reason: string }
  | { action: 'reopen' }
  | { action: 'withdraw_event'; event_id: string; reason: string }
  | { action: 'change_carrier'; carrier: string; reason?: string }
  | {
      action: 'pin_conveyance';
      conveyance_id?: string | null;
      imo?: string;
      mmsi?: string;
      name?: string;
    }
  | {
      action: 'change_numbers';
      containers?: string[];
      bill_of_lading?: string | null;
      booking?: string | null;
    };

export const actionResultSchema = z.looseObject({
  action: z.string(),
  shipment_id: z.string(),
  result: z.record(z.string(), z.unknown()),
});
export type ActionResult = z.infer<typeof actionResultSchema>;

/** `result` of `track_now`: `queued: false, waits_for: 'reader'` while no page reader is configured. */
export const trackNowResultSchema = z.looseObject({
  queued: z.boolean().optional(),
  next_check_at: z.string().nullable(),
  next_possible_at: z.string().nullable(),
  waits_for: z.string().nullable(),
  /** A person's click: dispatched at once (ADR 0096); `gate_skipped` when the 6 h limit was closed. */
  started: z.boolean().optional(),
  gate_skipped: z.boolean().optional(),
  already_running: z.boolean().optional(),
});
export type TrackNowResult = z.infer<typeof trackNowResultSchema>;

export const bulkResultSchema = z.looseObject({
  action: z.string(),
  ok: z.number(),
  failed: z.number(),
  items: z.array(
    z.looseObject({
      shipment_id: z.string(),
      ok: z.boolean(),
      result: z.record(z.string(), z.unknown()).nullable(),
      error: z.looseObject({ code: z.string(), detail: z.string().nullable() }).nullable(),
    }),
  ),
});

// ── calls ──────────────────────────────────────────────────────────────

const enc = encodeURIComponent;

export const shipmentsApi = {
  list: (search: URLSearchParams, signal?: AbortSignal) =>
    api(`/internal/shipments${search.size ? `?${search}` : ''}`, {
      schema: shipmentListSchema,
      ...(signal ? { signal } : {}),
    }),
  counters: () => api('/internal/shipments/counters', { schema: countersSchema }),
  views: () => api('/internal/shipments/views', { schema: savedViewListSchema }),
  createView: (body: { name: string; filters: Record<string, string>; sort: string | null }) =>
    api('/internal/shipments/views', { method: 'POST', body, schema: savedViewSchema }),
  removeView: (id: string) => api(`/internal/shipments/views/${enc(id)}`, { method: 'DELETE' }),
  detail: (id: string) => api(`/internal/shipments/${enc(id)}`, { schema: shipmentDetailSchema }),
  events: (id: string) =>
    api(`/internal/shipments/${enc(id)}/events?include=withdrawn`, { schema: eventsSchema }),
  etaEstimates: (id: string) =>
    api(`/internal/shipments/${enc(id)}/eta-estimates`, { schema: etaEstimatesSchema }),
  history: (id: string) => api(`/internal/shipments/${enc(id)}/history`, { schema: historySchema }),
  positions: (id: string, limit = 200) =>
    api(`/internal/shipments/${enc(id)}/positions?limit=${limit}`, { schema: positionsSchema }),
  reads: (id: string) => api(`/internal/shipments/${enc(id)}/reads`, { schema: readListSchema }),
  read: (readId: string) => api(`/internal/reads/${enc(readId)}`, { schema: readDetailSchema }),
  reportWrong: (readId: string, reason: string) =>
    api(`/internal/reads/${enc(readId)}/report-wrong`, {
      method: 'POST',
      body: { reason },
      schema: readDetailSchema,
    }),
  reExtract: (readId: string) =>
    api(`/internal/extraction/reads/${enc(readId)}/re-extract`, { method: 'POST' }),
  deliveries: (id: string) =>
    api(`/internal/shipments/${enc(id)}/deliveries`, { schema: shipmentDeliveriesSchema }),
  delivery: (deliveryId: string) =>
    api(`/internal/deliveries/${enc(deliveryId)}`, { schema: payloadViewSchema }),
  retryDelivery: (deliveryId: string) =>
    api(`/internal/deliveries/${enc(deliveryId)}/retry`, { method: 'POST' }),
  action: (id: string, body: ShipmentActionBody) =>
    api(`/internal/shipments/${enc(id)}/actions`, { method: 'POST', body, schema: actionResultSchema }),
  bulk: (action: 'track_now' | 'acknowledge', ids: string[]) =>
    api('/internal/shipments/bulk', { method: 'POST', body: { action, ids }, schema: bulkResultSchema }),
  ackAlert: (id: string, alertId: string) =>
    api(`/internal/shipments/${enc(id)}/alerts/${enc(alertId)}/ack`, { method: 'POST' }),
  snoozeAlert: (id: string, alertId: string, until: string) =>
    api(`/internal/shipments/${enc(id)}/alerts/${enc(alertId)}/snooze`, {
      method: 'POST',
      body: { until },
    }),
};

/** Query keys of the area: invalidate `['shipments']` after any action. */
export const shipmentKeys = {
  all: ['shipments'] as const,
  list: (search: string) => ['shipments', 'list', search] as const,
  counters: ['shipments', 'counters'] as const,
  views: ['shipments', 'views'] as const,
  detail: (id: string) => ['shipments', 'detail', id] as const,
  part: (id: string, part: string, ...rest: string[]) => ['shipments', 'detail', id, part, ...rest] as const,
  read: (readId: string) => ['shipments', 'read', readId] as const,
  delivery: (deliveryId: string) => ['shipments', 'delivery', deliveryId] as const,
};
